import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoMdEye, IoMdPrint } from "react-icons/io";
import { IoCopy } from "react-icons/io5";
import { FaMagic, FaPlus, FaCheck } from "react-icons/fa";
import { FiLoader, FiMonitor } from "react-icons/fi";
import { toast } from "react-toastify";
import RTE from "../../../components/RTE";
import DraggableSection from "./DraggableSection";
import MicPopover from "./MicPopover";
import FormulaPickerPopover from "./FormulaPickerPopover";
import FormulaInputDialog from "./FormulaInputDialog";
import { usePatientAuthFlags } from "../../../Hooks/usePatientAuthFlags";
import { useCrePermission } from "../../../Hooks/useCrePermission";
import { CRE_PERMISSION } from "../../../cre/crePermissions";
import { isHebrewReportLanguage } from "../../../cre/noTemplate";
import { sendAgentCommand } from "../../../agent/agentConnection";
import { getUserCredits } from "../../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../../i18n/hooks/useI18nLanguage";
import ModelSpeedSettingsPopover from "../../../components/common/ModelSpeedSettingsPopover";
import { useGlobalStore } from "../../../context/UserContext/userContext";
import { getReportClipboardPayload } from "../../../utils/reportClipboard";
import { parseInsertTextResponse } from "../../../utils/agentResponses";
import { COMPANION_APP_LABEL } from "../../../agent/constants";
import RadicalTrashIcon from "./RadicalTrashIcon";
import TemplateInstructionsField from "./TemplateInstructionsField";
import DarkModeToggle from "../../../components/darkmodetoogle/DarkModeToggle";
import { buildPatientTemplatesMacrosPath } from "../../../utils/buildPatientAuthUrl";
import { formatFormulaOutputText } from "../../../utils/formulaCalculationMarkers";
import { insertHtmlIntoReportEditor } from "../../../utils/insertHtmlIntoReportEditor";
import { replaceFormulaPendingChipWithText } from "../../../utils/formulaPendingChip";
import {
  useAgentEditorHeight,
  useCreAgentMode,
} from "../../../cre/agentMode/useCreAgentMode";

/**
 *
 * @param {Object} props
 * @param {string} props.id - Идентификатор для DnD
 * @param {string} props.transcript - Содержимое редактора
 * @param {boolean} props.loading - Флаг загрузки
 * @param {Object} props.listeners - Listeners для перетаскивания
 * @param {React.RefObject} props.rteRef - Ref для внешнего div вокруг редактора
 * @param {React.RefObject} props.editorRef - Ref для редактора Tiptap
 * @param {Function} props.onTranscriptChange - Обработчик изменения содержимого редактора
 * @param {Function} props.onProcessWithAI - Обработчик для обработки содержимого с помощью AI
 * @param {string} props.modelSpeed - ProcessWithAI ModelSpeed: "Smarter" | "Fastest"
 * @param {Function} props.setModelSpeed - Обновление model speed (сохраняется в cookie)
 * @param {boolean} [props.findingsOnly] - Findings-only mode (Radical): Restrictions.FindingsOnly
 * @param {Function} [props.onFindingsOnlyChange] - Toggle findings-only (enables settings UI when set)
 * @param {Function} props.onCopy - Обработчик копирования содержимого
 * @param {Function} props.onPrint - Обработчик печати отчета
 * @param {Function} props.onPreview - Обработчик предпросмотра отчета
 * @param {boolean} props.isEditorMicActive - Editor mic recording (popover when active)
 * @param {Function} props.onMicClick - Обработчик нажатия на кнопку микрофона
 * @param {Function} props.onReset - Radical: clear report (gray trash)
 * @param {string} [props.selectedTemplateName] - Selected template shown in editor header
 * @param {boolean} [props.noTemplateMode] - CRE: Process AI via global No Template
 * @param {Function} [props.onNoTemplateModeChange] - CRE: toggle No Template mode (must not clear dictation)
 * @param {number} [props.formulaQueueCount] - CRE: queued formulas for next Process AI
 * @param {Function} [props.onFormulaQueue] - CRE: enqueue formula for Process AI
 * @param {object|null} [props.formulaManualDialog] - CRE: open manual formula dialog state
 * @param {Function} [props.onFormulaManualOpen] - CRE: open manual calculate for a formula
 * @param {Function} [props.onFormulaManualClose] - CRE: close manual dialog
 * @returns {JSX.Element}
 */
const ReportEditorSection = ({
  id,
  transcript,
  loading,
  rteRef,
  editorRef,
  onTranscriptChange,
  onProcessWithAI,
  modelSpeed = "Smarter",
  setModelSpeed,
  findingsOnly = false,
  onFindingsOnlyChange,
  onCopy,
  onPrint,
  onPreview,
  isEditorMicActive = false,
  onMicClick,
  onEditorFocus,
  streamingTranscript,
  templateInstructions,
  setTemplateInstructions,
  language = "en",
  dictationLanguage = "en-US",
  isCommandMode = false,
  onReset,
  selectedTemplateName = "",
  noTemplateMode = false,
  onNoTemplateModeChange,
  formulaQueueCount = 0,
  onFormulaQueue,
  formulaManualDialog = null,
  onFormulaManualOpen,
  onFormulaManualClose,
}) => {
  const { t } = useTranslation("common");
  const { isRTL } = useI18nLanguage();
  const location = useLocation();
  const { isCreAuth, isRadicalAuth } = usePatientAuthFlags();
  // CRE Agent Mode: same CRE editor, filling the agent window.
  const { agentMode } = useCreAgentMode();
  const agentEditorHeight = useAgentEditorHeight(agentMode);
  const { requirePermission } = useCrePermission();
  const [inserting, setInserting] = useState(false);
  const [credits, setCredits] = useState(null);
  const [formulaPickerOpen, setFormulaPickerOpen] = useState(false);
  const formulaButtonRef = useRef(null);
  const radicalSelectedTemplateLabel = String(selectedTemplateName || "").trim()
    ? selectedTemplateName
    : t("noTemplateSelected", { defaultValue: "No template selected" });
  const noTemplateTooltip = t("noTemplateModeTooltip", {
    defaultValue: "General Report / No Template",
  });
  const showNoTemplateControl =
    isCreAuth &&
    isHebrewReportLanguage(language) &&
    typeof onNoTemplateModeChange === "function";
  const showFormulaControls =
    isCreAuth && typeof onFormulaQueue === "function";
  // Template Instructions follow the selected (Patient) language prop, which stays
  // in sync with the navbar switcher; the local useI18nLanguage state can be stale.
  const languageBase = String(language || "").toLowerCase().split(/[-_]/)[0];
  const isTemplateInstructionsRtl =
    isRTL || languageBase === "he" || languageBase === "ar";

  const handleManualFormulaSuccess = (payload) => {
    const text = formatFormulaOutputText({
      ...payload,
      isEstimated: payload?.isEstimated,
    });
    if (!text) {
      toast.error(
        t("formulaEngine.emptyResult", {
          defaultValue: "Calculation returned no text.",
        }),
      );
      return;
    }
    const editor = editorRef?.current?.editor;
    if (!editor) {
      toast.warning(t("editorNotReady", { defaultValue: "Editor not ready" }));
      return;
    }

    const calculationId = formulaManualDialog?.calculationId;
    // Prefer replacing the Process AI pending chip at the LLM marker position.
    if (calculationId) {
      const replaced = replaceFormulaPendingChipWithText(
        editor,
        calculationId,
        text,
        onTranscriptChange,
      );
      if (replaced) {
        if (payload?.warning) toast.warning(String(payload.warning));
        toast.success(
          t("formulaEngine.inserted", {
            defaultValue: "Calculation inserted.",
          }),
        );
        return;
      }
    }

    // No chip: insert as plain TipTap text at captured caret / current selection.
    // Never append at document end (Vigen contract).
    const insertPos =
      formulaManualDialog?.insertPos != null
        ? formulaManualDialog.insertPos
        : null;
    const result = insertHtmlIntoReportEditor({
      editor,
      text,
      setTranscript: onTranscriptChange,
      insertPos,
      allowAppend: false,
      onError: () => {
        toast.error(
          t("formulaEngine.insertFailed", {
            defaultValue: "Failed to insert calculation into the report.",
          }),
        );
      },
    });
    if (result?.ok) {
      if (payload?.warning) toast.warning(String(payload.warning));
      toast.success(
        t("formulaEngine.inserted", {
          defaultValue: "Calculation inserted.",
        }),
      );
    }
  };

  const captureEditorInsertPos = () => {
    const editor = editorRef?.current?.editor;
    const from = editor?.state?.selection?.from;
    return Number.isFinite(from) ? from : null;
  };
  const creCircularButtonClass =
    "flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-900/10 text-gray-900 shadow-sm transition-colors dark:border-gray-700 dark:text-black disabled:opacity-50 disabled:cursor-not-allowed";
  const creCircularButtonIdle =
    "bg-[#CBEA7B80] hover:bg-[#A4C639] hover:shadow-md dark:hover:bg-[#CBEA7B] dark:hover:text-black";
  const creCircularButtonActive =
    "bg-[#A4C639] shadow-md dark:bg-[#CBEA7B] dark:text-black";

  const handleInsertToWindow = React.useCallback(async () => {
    if (!requirePermission(CRE_PERMISSION.WINDOWS_INJECTION)) {
      return;
    }
    const payload = getReportClipboardPayload({
      editor: editorRef.current?.editor,
      transcript,
      rteRef,
    });
    if (!payload.plainText.trim()) {
      toast.error(t("companionAgent.emptyReport"));
      return;
    }

    setInserting(true);
    try {
      const token = await useGlobalStore.getState().getAccessToken();
      const resp = await sendAgentCommand(
        { action: "insert_text", text: payload.plainText },
        token,
      );
      const result = parseInsertTextResponse(resp);
      if (result.notConnected) {
        toast.error(
          t("companionAgent.notConnected", { app: COMPANION_APP_LABEL }),
        );
        return;
      }
      if (result.ok) {
        toast.success(result.message || t("companionAgent.insertSuccess"));
        return;
      }
      toast.error(result.message || t("companionAgent.insertFailed"));
    } catch (error) {
      console.error("Insert to window failed:", error);
      toast.error(t("companionAgent.insertFailed"));
    } finally {
      setInserting(false);
    }
  }, [editorRef, rteRef, t, transcript, requirePermission]);

  const fetchCredits = React.useCallback(async () => {
    if (!isCreAuth) return;
    const res = await getUserCredits();
    if (res?.success) {
      const { totalCredits = 0, usedCredits = 0, remainingCredits = 0 } =
        res.payload || {};
      if (totalCredits === -1) {
        setCredits({ unlimited: true });
        return;
      }
      setCredits({
        unlimited: false,
        total: totalCredits,
        used: usedCredits,
        remaining: remainingCredits,
      });
    }
  }, [isCreAuth]);

  React.useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const showCreditsMeter =
    isCreAuth && credits && !credits.unlimited;

  return (
    <DraggableSection
      id={id}
      className={`${agentMode ? "" : "lg:w-4/6 "}w-full rounded-2xl bg-white dark:bg-gray-800 shadow-sm ${
        isRadicalAuth ? "flex flex-col min-h-[calc(100vh-6rem)]" : ""
      }`}
    >
      <div
        className={`w-full overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-sm ${
          isRadicalAuth ? "flex flex-col flex-1 min-h-0" : ""
        }`}
      >
        <div className="sticky top-0 z-20 rounded-t-2xl bg-white p-3 dark:bg-gray-800">
          {isRadicalAuth ? (
            <div className="relative flex w-full items-center gap-1">
              <Link to={buildPatientTemplatesMacrosPath(location.search)}>
                <button
                  type="button"
                  className="flex shrink-0 flex-row items-center justify-center gap-1 rounded-lg bg-gray-200 px-2 py-1.5 text-[11px] font-semibold tracking-wide text-gray-800 shadow-sm transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:px-2.5 sm:py-2 sm:text-xs"
                  title={t("createTemplatesAndMacros")}
                >
                  <FaPlus
                    size={10}
                    className="shrink-0 text-gray-700 dark:text-gray-300"
                  />
                  <span>T & M</span>
                </button>
              </Link>
              <div className="flex items-center px-0.5">
                <DarkModeToggle />
              </div>
              {onReset && (
                <button
                  type="button"
                  className="flex w-7 h-7 sm:w-8 sm:h-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
                  onClick={onReset}
                  title={t("deleteReport")}
                  aria-label={t("deleteReport")}
                >
                  <RadicalTrashIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-900 dark:text-gray-100" />
                </button>
              )}
              <div className="absolute left-1/2 top-1/2 max-w-[min(52%,14rem)] -translate-x-1/2 -translate-y-1/2 sm:max-w-[min(58%,18rem)]">
                <div
                  className="truncate rounded-full bg-gray-100 px-2.5 py-1 text-center text-[16px] font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-100 sm:px-3 sm:py-1.5"
                  title={radicalSelectedTemplateLabel}
                >
                  {radicalSelectedTemplateLabel}
                </div>
              </div>
              {isEditorMicActive && (
                <MicPopover
                  isActive={isEditorMicActive}
                  streamingTranscript={streamingTranscript}
                  dictationLanguage={dictationLanguage}
                  isCommandMode={isCommandMode}
                  onStop={onMicClick}
                  isRadicalAuth={isRadicalAuth}
                />
              )}
            </div>
          ) : (
            <div className="flex w-full flex-col gap-5 md:grid md:grid-cols-3 md:items-center md:gap-0">
              {!isCreAuth && (
                <div
                  className="hidden md:block w-64 shrink-0"
                  aria-hidden="true"
                />
              )}
              {isCreAuth &&
                (showCreditsMeter ? (
                  <div className="w-full md:w-64 shrink-0">
                    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                      <span>Credits</span>
                      <span>
                        {credits.used}/{credits.total}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#A4C639] transition-all"
                        style={{
                          width: `${(credits.used /
                            Math.max(credits.total || 1, 1)) *
                            100}%`,
                        }}
                      />
                    </div>
                    {credits.remaining <= 0 && (
                      <button
                        className="mt-2 w-full bg-[#CBEA7B80] hover:bg-[#A4C639] text-gray-800 text-xs px-3 py-1 rounded-full transition-colors font-medium"
                        onClick={() =>
                          window.open(
                            "https://www.medilis.ai/products",
                            "_blank",
                          )
                        }
                      >
                        Purchase Credits
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    className="hidden md:block w-64 shrink-0"
                    aria-hidden="true"
                  />
                ))}
              <div className="flex min-w-0 flex-row flex-wrap items-center justify-center gap-1 md:justify-center">
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center gap-0 rounded-full border border-gray-900/10 bg-[#CBEA7B80] text-gray-900 shadow-sm transition-colors hover:bg-[#A4C639] hover:shadow-md dark:border-gray-700 dark:text-black dark:hover:bg-[#CBEA7B] dark:hover:text-black xl:h-10 xl:w-auto xl:min-w-0 xl:gap-2 xl:px-4 xl:py-2 xl:text-sm xl:font-semibold"
                  aria-label={t("processReportButton")}
                  title={t("processReportButton")}
                  onClick={async () => {
                    try {
                      const result = await onProcessWithAI();
                      // Soft-fail returns { ok: false } (e.g. No Template abort)
                      // without throwing — only refresh after a real success.
                      if (result?.ok === true) {
                        await fetchCredits();
                      }
                    } catch (error) {
                      // Thrown failures: do not refresh credits
                      console.log("error in AI processing", error);
                    }
                  }}
                >
                  <FaMagic
                    className="shrink-0 text-base text-gray-900 dark:text-black"
                    aria-hidden
                  />
                  <span
                    className={`hidden font-semibold tracking-wide whitespace-nowrap xl:inline ${
                      isRTL ? "" : "uppercase"
                    }`}
                  >
                    {t("processReportButton")}
                  </span>
                </button>
                <ModelSpeedSettingsPopover
                  value={modelSpeed}
                  onChange={setModelSpeed}
                  disabled={!setModelSpeed}
                  isRTL={isRTL}
                  groupLabel={t("modelSpeed")}
                  groupHint={t("modelSpeedHint")}
                  segmentSmarterLabel={t("modelSpeedSegmentSmarter")}
                  segmentFastestLabel={t("modelSpeedSegmentFastest")}
                  smarterTitle={t("modelSpeedSmarter")}
                  fastestTitle={t("modelSpeedFastest")}
                  settingsButtonTitle={t("findingsOnly")}
                  findingsOnly={findingsOnly}
                  onFindingsOnlyChange={
                    !isCreAuth ? onFindingsOnlyChange : undefined
                  }
                  findingsOnlyLabel={t("findingsOnly")}
                  findingsOnlyHint={t("findingsOnlyHint")}
                />
                {isCreAuth && (
                  <button
                    type="button"
                    className={`${creCircularButtonClass} ${creCircularButtonIdle}`}
                    onClick={() => void handleInsertToWindow()}
                    disabled={inserting}
                    title={t("companionAgent.insert")}
                    aria-label={t("companionAgent.insert")}
                  >
                    {inserting ? (
                      <FiLoader className="animate-spin text-base text-gray-900 dark:text-black" />
                    ) : (
                      <FiMonitor className="text-base text-gray-900 dark:text-black" />
                    )}
                  </button>
                )}
              </div>
              {isCreAuth ? (
                <div className="grid w-full min-w-0 grid-cols-3 items-center gap-2 md:flex md:items-center md:justify-between">
                  <div className="min-w-0 md:hidden" aria-hidden="true" />
                  <div className="flex min-w-0 justify-center md:mx-auto md:justify-start">
                    <div
                      className="truncate rounded-full bg-gray-100 px-2.5 py-1 text-center text-[16px] font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-100 sm:px-3 sm:py-1.5"
                      title={radicalSelectedTemplateLabel}
                    >
                      {radicalSelectedTemplateLabel}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 md:justify-end">
                    {isEditorMicActive && (
                      <MicPopover
                        isActive={isEditorMicActive}
                        streamingTranscript={streamingTranscript}
                        dictationLanguage={dictationLanguage}
                        isCommandMode={isCommandMode}
                        onStop={onMicClick}
                        isRadicalAuth={isRadicalAuth}
                      />
                    )}
                    {showNoTemplateControl && (
                      <button
                        type="button"
                        className={`${creCircularButtonClass} ${
                          noTemplateMode
                            ? creCircularButtonActive
                            : creCircularButtonIdle
                        }`}
                        onClick={() => onNoTemplateModeChange(!noTemplateMode)}
                        title={noTemplateTooltip}
                        aria-label={noTemplateTooltip}
                        aria-pressed={!!noTemplateMode}
                      >
                        <span
                          className={`flex h-4 w-4 items-center justify-center rounded-sm border-2 ${
                            noTemplateMode
                              ? "border-gray-900 bg-gray-900 text-[#CBEA7B] dark:border-black dark:bg-black dark:text-[#CBEA7B]"
                              : "border-gray-900 dark:border-black"
                          }`}
                          aria-hidden
                        >
                          {noTemplateMode ? (
                            <FaCheck className="text-[10px]" />
                          ) : null}
                        </span>
                      </button>
                    )}
                    {showFormulaControls && (
                      <div className="relative" ref={formulaButtonRef}>
                        <button
                          type="button"
                          className={`${creCircularButtonClass} ${creCircularButtonIdle}`}
                          onClick={() => setFormulaPickerOpen((v) => !v)}
                          title={t("formulaEngine.pickerTitle", {
                            defaultValue: "Formulas",
                          })}
                          aria-label={t("formulaEngine.pickerTitle", {
                            defaultValue: "Formulas",
                          })}
                          aria-expanded={formulaPickerOpen}
                        >
                          <span className="text-sm font-bold italic text-gray-900 dark:text-black">
                            ƒ
                          </span>
                          {formulaQueueCount > 0 ? (
                            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-900 px-0.5 text-[9px] text-[#CBEA7B] dark:bg-black">
                              {formulaQueueCount}
                            </span>
                          ) : null}
                        </button>
                        <FormulaPickerPopover
                          open={formulaPickerOpen}
                          onClose={() => setFormulaPickerOpen(false)}
                          anchorRef={formulaButtonRef}
                          onQueue={(formula) => {
                            onFormulaQueue?.(formula);
                            toast.success(
                              t("formulaEngine.queued", {
                                defaultValue:
                                  "Formula queued for Process AI.",
                              }),
                            );
                          }}
                          onManual={(formula) =>
                            onFormulaManualOpen?.(formula, {
                              insertPos: captureEditorInsertPos(),
                            })
                          }
                        />
                      </div>
                    )}
                    <button
                      className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                      onClick={onPreview}
                      title="Preview"
                    >
                      <IoMdEye className="text-gray-900 dark:text-gray-100" />
                    </button>
                    <button
                      className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                      onClick={onPrint}
                      title="Print"
                    >
                      <IoMdPrint className="text-gray-900 dark:text-gray-100" />
                    </button>
                    <button
                      className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                      onClick={onCopy}
                      title="Copy"
                    >
                      <IoCopy className="text-gray-900 dark:text-gray-100" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 md:justify-end">
                  {isEditorMicActive && (
                    <MicPopover
                      isActive={isEditorMicActive}
                      streamingTranscript={streamingTranscript}
                      dictationLanguage={dictationLanguage}
                      isCommandMode={isCommandMode}
                      onStop={onMicClick}
                      isRadicalAuth={isRadicalAuth}
                    />
                  )}
                  <button
                    className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                    onClick={onPreview}
                    title="Preview"
                  >
                    <IoMdEye className="text-gray-900 dark:text-gray-100" />
                  </button>
                  <button
                    className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                    onClick={onPrint}
                    title="Print"
                  >
                    <IoMdPrint className="text-gray-900 dark:text-gray-100" />
                  </button>
                  <button
                    className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700"
                    onClick={onCopy}
                    title="Copy"
                  >
                    <IoCopy className="text-gray-900 dark:text-gray-100" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div
          className={`relative ${
            isRadicalAuth ? "flex-1 min-h-0 flex flex-col" : ""
          }`}
        >
          <div
            ref={rteRef}
            data-cre-report-root
            className={`w-full ${isRadicalAuth ? "flex-1 min-h-0" : ""}`}
          >
            <RTE
              name="editor"
              heightValue={
                agentMode
                  ? agentEditorHeight
                  : isCreAuth
                    ? 900
                    : 980
              }
              defaultValue={transcript}
              value={transcript}
              onChange={onTranscriptChange}
              editorRef={editorRef}
              language={language}
              onFocus={onEditorFocus}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 z-10">
                <div className="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-[#CBEA7B] dark:border-[#A4C639]"></div>
              </div>
            )}
            {isEditorMicActive && (
              <div className="absolute bottom-4 right-4 bg-green dark:bg-green-dark text-gray-900 dark:text-gray-100 px-3 py-1 rounded-full text-sm animate-pulse">
                Streaming...
              </div>
            )}
          </div>
        </div>
        {isCreAuth && (
          <div className="p-4 border-t dark:border-gray-700">
            <div className="flex flex-col gap-4">
              <TemplateInstructionsField
                value={templateInstructions}
                onChange={setTemplateInstructions}
                isRtl={isTemplateInstructionsRtl}
                showResidentPreset={isHebrewReportLanguage(language)}
              />
            </div>
          </div>
        )}
      </div>
      {formulaManualDialog?.formula ? (
        <FormulaInputDialog
          formula={formulaManualDialog.formula}
          missingParameters={formulaManualDialog.missingParameters}
          message={formulaManualDialog.message}
          blockedCode={formulaManualDialog.code}
          onClose={() => onFormulaManualClose?.()}
          onSuccess={handleManualFormulaSuccess}
        />
      ) : null}
    </DraggableSection>
  );
};

export default ReportEditorSection;
